import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import {
  gmailMessageDetailEndpoint,
  gmailMessagesEndpoint,
} from "@/config/endpoints";
import type { EmailMessage, EmailMessageStatus } from "../types/gmail.types";

/**
 * Sent / Drafts mailbox feeds.
 *
 * V1 has no Drafts persistence yet, so we only ship the EmailMessage list
 * here. The Drafts mailbox renders an empty-state until Phase 4b grows
 * EmailDraft endpoints.
 */
export const GMAIL_MESSAGES_QUERY_KEY = "gmail-messages";

export interface UseGmailMessagesParams {
  status?: EmailMessageStatus;
  leadId?: number;
  gmailAccountId?: number;
  cursor?: number;
  limit?: number;
}

export interface MessagesPage {
  items: EmailMessage[];
  nextCursor: number | null;
}

function adaptMessage(raw: any): EmailMessage {
  return {
    id: raw.id,
    gmailAccountId: raw.gmailAccountId,
    userId: raw.userId,
    gmailMessageId: raw.gmailMessageId ?? null,
    gmailThreadId: raw.gmailThreadId ?? null,
    leadId: raw.leadId ?? null,
    dealId: raw.dealId ?? null,
    templateId: raw.templateId ?? null,
    subject: raw.subject ?? "",
    htmlBody: raw.htmlBody ?? "",
    plainBody: raw.plainBody ?? null,
    fromAddress: raw.fromAddress ?? "",
    toAddresses: raw.toAddresses ?? [],
    ccAddresses: raw.ccAddresses ?? [],
    bccAddresses: raw.bccAddresses ?? [],
    attachments: raw.attachments ?? null,
    status: raw.status,
    sentAt: raw.sentAt ?? null,
    errorMessage: raw.errorMessage ?? null,
    errorCode: raw.errorCode ?? null,
    triggeredByAutomationId: raw.triggeredByAutomationId ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

export function useGmailMessages(params: UseGmailMessagesParams = {}) {
  const query = useQuery({
    queryKey: [GMAIL_MESSAGES_QUERY_KEY, params],
    queryFn: async () => {
      const res = await axiosInstance.get(gmailMessagesEndpoint, { params });
      const raw = res.data?.data ?? { items: [], nextCursor: null };
      const list = raw.items ?? raw.messages ?? [];
      return {
        items: list.map(adaptMessage),
        nextCursor: raw.nextCursor ?? null,
      } as MessagesPage;
    },
    staleTime: 1000 * 60,
  });

  return {
    items: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useGmailMessage(id: number | null) {
  const query = useQuery({
    queryKey: [GMAIL_MESSAGES_QUERY_KEY, "detail", id],
    queryFn: async () => {
      const url = gmailMessageDetailEndpoint.replace(":id", String(id));
      const res = await axiosInstance.get(url);
      return adaptMessage(res.data?.data);
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60,
  });

  return {
    message: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
